import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  storage,
  type Client,
  type TenantProperty,
  type TenantBooking,
  type TenantOverview,
  type TenantOnboarding,
} from '../lib/api';
import { useClient, useDeleteClient, useUpdateClient, useTenantOverview } from '../hooks/useClients';
import { useInviteClient } from '../hooks/useProjects';
import {
  Trash2, ExternalLink, Edit3, Loader2, Phone, Mail, Globe, Users, BarChart3,
  Home, Calendar, MessageSquare, CreditCard, CheckCircle2, XCircle, FileSignature, User as UserIcon,
} from 'lucide-react';
import { format } from 'date-fns';
import PageHeader from '../components/PageHeader';

export default function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const numericId = Number(id);

  const { data: clientData, isLoading } = useClient(numericId);
  const { data: overview, isLoading: overviewLoading } = useTenantOverview(numericId);

  const client = clientData?.client ?? null;

  const updateClientMut = useUpdateClient();
  const [editing, setEditing] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const deleteClient = useDeleteClient();
  const inviteMut = useInviteClient();
  const [inviteSent, setInviteSent] = useState(false);

  const handleInvite = () => {
    if (!client) return;
    if (!window.confirm(`¿Enviar la invitación de acceso al panel a ${client.contact_email || client.business_name}?`)) return;
    inviteMut.mutate(client.id, { onSuccess: () => setInviteSent(true) });
  };

  const handleDelete = () => {
    if (!client) return;
    deleteClient.mutate(client.id, {
      onSuccess: () => navigate('/clients'),
    });
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-gray-200 rounded w-48" />
        <div className="h-64 bg-gray-100 rounded-xl" />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-500">Tenant no encontrado</p>
        <Link to="/clients" className="mt-4 inline-block text-blue-600 hover:text-blue-700 text-sm font-medium">
          ← Volver a tenants
        </Link>
      </div>
    );
  }

  const stats = overview?.booking_stats;

  return (
    <div className="space-y-6">
      <PageHeader
        type="client"
        icon={Users}
        title={
          <span className="flex items-center gap-3">
            {client.logo_key ? (
              <img src={storage.inlineUrl(client.logo_key)} alt="" className="w-8 h-8 rounded-full object-contain bg-gray-50" />
            ) : (
              <span className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm">
                {client.business_name.charAt(0).toUpperCase()}
              </span>
            )}
            {client.business_name}
          </span>
        }
        subtitle={
          <div className="flex items-center gap-2">
            <ClientStatusBadge status={client.status} />
            <OnboardingBadge status={overview?.client.onboarding_status ?? null} />
            {client.domain && (
              <a href={`https://${client.domain}`} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
                {client.domain} <ExternalLink size={12} />
              </a>
            )}
          </div>
        }
        backLink="/clients"
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (!client) return;
                const newVal = client.count_in_stats ? 0 : 1;
                updateClientMut.mutate({ id: client.id, data: { count_in_stats: newVal } });
              }}
              className={`flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm transition-colors ${
                client.count_in_stats
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                  : 'border-gray-200 text-gray-400 hover:bg-gray-50'
              }`}
              title={client.count_in_stats ? 'Incluido en estadísticas públicas' : 'No incluido en estadísticas públicas'}
            >
              <BarChart3 size={14} />
              Stats
            </button>
            <Link
              to={`/tenant-site/${client.id}`}
              className="flex items-center gap-2 px-3 py-2 border border-teal-200 bg-teal-50 text-teal-700 rounded-lg text-sm hover:bg-teal-100 transition-colors"
              title="Editar la web del tenant"
            >
              <Globe size={14} />
              Editar Web
            </Link>
            <button
              onClick={handleInvite}
              disabled={inviteMut.isPending || inviteSent}
              className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm transition-colors disabled:opacity-60 ${
                inviteSent
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                  : 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100'
              }`}
              title="Enviar al propietario un enlace de acceso a su panel"
            >
              <Mail size={14} />
              {inviteMut.isPending ? 'Enviando…' : inviteSent ? 'Invitación enviada' : 'Invitar al panel'}
            </button>
            <button
              onClick={() => setEditing(true)}
              className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <Edit3 size={14} />
              Editar
            </button>
            <button
              onClick={() => setShowDelete(true)}
              className="p-2 rounded-lg border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200 transition-colors"
              title="Eliminar"
            >
              <Trash2 size={16} />
            </button>
          </div>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Propiedades" value={overview ? String(overview.properties.length) : '…'} icon={Home} />
        <KpiCard label="Reservas" value={stats ? String(stats.total) : '…'} icon={Calendar} />
        <KpiCard label="Confirmadas" value={stats ? String(stats.confirmed ?? 0) : '…'} icon={CheckCircle2} />
        <KpiCard label="Ingresos" value={stats ? formatMoney(stats.revenue) : '…'} icon={CreditCard} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-6">
          <PropertiesSection properties={overview?.properties ?? []} loading={overviewLoading} />
          <BookingsSection bookings={overview?.bookings ?? []} loading={overviewLoading} />
          <FeedbackSection feedback={overview?.feedback ?? []} loading={overviewLoading} />
        </div>

        {/* Right column */}
        <div className="space-y-6">
          <OwnerSection overview={overview} clientCreatedAt={client.created_at} />
          <BillingSection overview={overview} />
          <WebsiteSection overview={overview} clientId={client.id} />
          {client.notes && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-2">
              <h3 className="font-semibold text-gray-900">Notas internas</h3>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{client.notes}</p>
            </div>
          )}
        </div>
      </div>

      {editing && (
        <EditClientModal
          client={client}
          onClose={() => setEditing(false)}
          onUpdated={() => setEditing(false)}
        />
      )}

      {showDelete && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowDelete(false)}>
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900">Eliminar tenant</h3>
            <p className="text-sm text-gray-500 mt-2">
              ¿Eliminamos <strong>{client.business_name}</strong>? Se borrarán también sus propiedades, reservas y web.
            </p>
            <div className="flex gap-3 mt-5 justify-end">
              <button onClick={() => setShowDelete(false)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600">
                Cancelar
              </button>
              <button onClick={handleDelete} disabled={deleteClient.isPending} className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium disabled:opacity-50">
                {deleteClient.isPending ? 'Eliminando…' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sections ──────────────────────────────────────────────────────────────────

function KpiCard({ label, value, icon: Icon }: { label: string; value: string; icon: typeof Home }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center shrink-0">
        <Icon size={18} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-400">{label}</p>
        <p className="text-lg font-bold text-gray-900 truncate">{value}</p>
      </div>
    </div>
  );
}

function OwnerSection({ overview, clientCreatedAt }: { overview: TenantOverview | undefined; clientCreatedAt: string }) {
  const owners = overview?.owners ?? [];
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
      <h3 className="font-semibold text-gray-900 flex items-center gap-1.5"><UserIcon size={14} /> Propietario / Cuenta</h3>
      {owners.length === 0 ? (
        <p className="text-xs text-gray-400">Sin cuenta de cliente registrada todavía.</p>
      ) : (
        <div className="space-y-3">
          {owners.map(o => (
            <div key={o.id} className="text-sm space-y-1">
              <p className="font-medium text-gray-800">{o.name}</p>
              <a href={`mailto:${o.email}`} className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-xs break-all">
                <Mail size={12} /> {o.email}
              </a>
              <p className="text-xs text-gray-400">
                {o.last_login_at ? `Último acceso: ${fmtDate(o.last_login_at)}` : 'Aún no ha iniciado sesión'}
              </p>
            </div>
          ))}
        </div>
      )}
      <div className="pt-3 border-t border-gray-50 text-xs text-gray-400">
        Tenant creado: {fmtDate(clientCreatedAt)}
      </div>
    </div>
  );
}

function BillingSection({ overview }: { overview: TenantOverview | undefined }) {
  const ob = overview?.onboarding;
  const ag = overview?.agreement;
  const connectStatus = overview?.client.stripe_account_status ?? null;
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
      <h3 className="font-semibold text-gray-900 flex items-center gap-1.5"><CreditCard size={14} /> Alta y facturación</h3>
      <dl className="space-y-3 text-sm">
        <Row label="Modo de configuración">
          <SetupModeBadge mode={ob?.setup_mode ?? null} />
        </Row>
        <Row label="Acuerdo firmado">
          {ag ? (
            <span className="text-emerald-700 flex items-center gap-1">
              <FileSignature size={13} /> {ag.accepted_name} · {fmtDate(ag.accepted_at)}
            </span>
          ) : (
            <span className="text-gray-400 flex items-center gap-1"><XCircle size={13} /> No firmado</span>
          )}
        </Row>
        <Row label="Plan">
          {ob?.plan_units ? (
            <span className="text-gray-700">
              {ob.plan_units} {ob.plan_units === 1 ? 'propiedad' : 'propiedades'}
              {ob.plan_billing ? ` · ${ob.plan_billing === 'yearly' ? 'anual' : 'mensual'}` : ''}
            </span>
          ) : (
            <span className="text-gray-400">Sin plan</span>
          )}
        </Row>
        <Row label="Modelo de web">
          <span className="text-gray-700">
            {ob?.site_model === 'per_property' ? 'Una web por propiedad' : 'Web combinada'}
          </span>
        </Row>
        <Row label="Pago suscripción">
          <PaymentBadge status={ob?.payment_status ?? null} paidAt={ob?.paid_at ?? null} />
        </Row>
        <Row label="Stripe Connect">
          <ConnectBadge status={connectStatus} hasAccount={!!overview?.client.stripe_account_id} />
        </Row>
        {ob?.legal_name && <Row label="Razón social"><span className="text-gray-700">{ob.legal_name}</span></Row>}
        {ob?.tax_id && <Row label="NIF/CIF"><span className="text-gray-700">{ob.tax_id}</span></Row>}
        {ob?.contact_phone && (
          <Row label="Teléfono">
            <a href={`tel:${ob.contact_phone}`} className="text-blue-600 hover:text-blue-700 flex items-center gap-1"><Phone size={12} /> {ob.contact_phone}</a>
          </Row>
        )}
      </dl>
      <ListingLinksBlock ob={ob} />
    </div>
  );
}

// Existing-listing URLs the owner provided — where the team copies their data from.
// Especially relevant when setup_mode === 'done_for_you'.
function ListingLinksBlock({ ob }: { ob: TenantOnboarding | null | undefined }) {
  if (!ob) return null;
  const links: { label: string; url: string | null }[] = [
    { label: 'Web actual', url: ob.current_website },
    { label: 'Booking.com', url: ob.listing_booking },
    { label: 'Airbnb', url: ob.listing_airbnb },
    { label: 'Vrbo', url: ob.listing_vrbo },
    { label: 'Otro', url: ob.listing_other },
    // Multiple done-for-you listing URLs (one per property), captured at onboarding.
    ...(ob.listing_urls ?? []).map((url, i) => ({ label: `Anuncio ${i + 1}`, url })),
  ].filter(l => l.url && l.url.trim());
  if (links.length === 0) return null;
  return (
    <div className="pt-3 border-t border-gray-50">
      <p className="text-xs font-medium text-gray-400 mb-2">Anuncios existentes (para copiar)</p>
      <div className="space-y-1.5">
        {links.map(l => (
          <div key={l.label} className="flex items-center justify-between gap-3 text-sm">
            <span className="text-gray-400 shrink-0">{l.label}</span>
            <a href={l.url!} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 flex items-center gap-1 truncate">
              <span className="truncate">{l.url}</span> <ExternalLink size={11} className="shrink-0" />
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}

function SetupModeBadge({ mode }: { mode: string | null }) {
  if (mode === 'done_for_you') {
    return <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-teal-100 text-teal-700">Lo hacemos nosotros</span>;
  }
  if (mode === 'self') {
    return <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Autoservicio</span>;
  }
  return <span className="text-xs text-gray-400">Sin elegir</span>;
}

function WebsiteSection({ overview, clientId }: { overview: TenantOverview | undefined; clientId: number }) {
  const site = overview?.site;
  const published = !!site?.has_published;
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
      <h3 className="font-semibold text-gray-900 flex items-center gap-1.5"><Globe size={14} /> Web del tenant</h3>
      <div className="text-sm space-y-2">
        <div className="flex items-center gap-2">
          {published ? (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">Publicada</span>
          ) : site?.has_draft ? (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Borrador</span>
          ) : (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Sin crear</span>
          )}
          {site?.published_at && <span className="text-xs text-gray-400">{fmtDate(site.published_at)}</span>}
        </div>
        {published && site?.site_slug && (
          <a
            href={`/site/${site.site_slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-700 text-xs flex items-center gap-1 break-all"
          >
            <ExternalLink size={12} /> /site/{site.site_slug}
          </a>
        )}
      </div>
      <Link
        to={`/tenant-site/${clientId}`}
        className="flex items-center gap-2 w-full justify-center px-4 py-2 border border-teal-200 bg-teal-50 text-teal-700 rounded-lg text-sm font-medium hover:bg-teal-100 transition-colors"
      >
        <Edit3 size={14} /> Abrir editor
      </Link>
    </div>
  );
}

function PropertiesSection({ properties, loading }: { properties: TenantProperty[]; loading: boolean }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <h3 className="font-semibold text-gray-900 flex items-center gap-1.5"><Home size={14} /> Propiedades ({properties.length})</h3>
      </div>
      {loading ? (
        <div className="p-8 text-center text-gray-400 text-sm animate-pulse">Cargando…</div>
      ) : properties.length === 0 ? (
        <div className="p-8 text-center text-gray-400 text-sm">Este tenant aún no tiene propiedades</div>
      ) : (
        <div className="divide-y divide-gray-50">
          {properties.map(p => {
            const amenities = countAmenities(p.amenities_json);
            return (
              <div key={p.id} className="px-5 py-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
                  <p className="text-xs text-gray-400">
                    {[p.property_type, p.city].filter(Boolean).join(' · ') || 'Sin ubicación'}
                  </p>
                </div>
                <div className="hidden sm:flex items-center gap-4 text-xs text-gray-500 shrink-0">
                  {p.max_guests != null && <span title="Huéspedes max."><Users size={12} className="inline mr-0.5" />{p.max_guests}</span>}
                  {p.bedrooms != null && <span title="Dormitorios">{p.bedrooms} dorm.</span>}
                  <span title="Fotos">{p.photo_count} fotos</span>
                  {amenities > 0 && <span title="Amenidades">{amenities} amen.</span>}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-semibold text-gray-900">{p.base_price != null ? formatMoney(p.base_price) : '—'}</p>
                  <PropertyStatusBadge status={p.status} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BookingsSection({ bookings, loading }: { bookings: TenantBooking[]; loading: boolean }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <h3 className="font-semibold text-gray-900 flex items-center gap-1.5"><Calendar size={14} /> Reservas recientes</h3>
      </div>
      {loading ? (
        <div className="p-8 text-center text-gray-400 text-sm animate-pulse">Cargando…</div>
      ) : bookings.length === 0 ? (
        <div className="p-8 text-center text-gray-400 text-sm">Sin reservas</div>
      ) : (
        <div className="divide-y divide-gray-50">
          {bookings.map(b => (
            <div key={b.id} className="px-5 py-3 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{b.guest_name}</p>
                <p className="text-xs text-gray-400 truncate">
                  {b.property_name ?? 'Propiedad eliminada'} · {fmtDay(b.check_in)} → {fmtDay(b.check_out)} ({b.nights}n)
                </p>
              </div>
              <span className="hidden sm:inline text-[10px] uppercase tracking-wide text-gray-400 shrink-0">{b.channel}</span>
              <BookingStatusBadge status={b.status} />
              <span className="text-sm font-semibold text-gray-900 shrink-0 w-20 text-right">{formatMoney(b.total, b.currency)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FeedbackSection({ feedback, loading }: { feedback: TenantOverview['feedback']; loading: boolean }) {
  if (!loading && feedback.length === 0) return null;
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-3">
      <h3 className="font-semibold text-gray-900 flex items-center gap-1.5"><MessageSquare size={14} /> Feedback del cliente ({feedback.length})</h3>
      {loading ? (
        <p className="text-sm text-gray-400 animate-pulse">Cargando…</p>
      ) : (
        <div className="space-y-3 max-h-80 overflow-auto">
          {feedback.map(f => (
            <div key={f.id} className="text-sm border-b border-gray-50 pb-2 last:border-0 last:pb-0">
              <div className="flex items-center gap-2 mb-0.5">
                <FeedbackStatusBadge status={f.status} />
                <span className="text-xs text-gray-400">{fmtDate(f.created_at)}</span>
                {f.page && <span className="text-xs text-gray-300 truncate">· {f.page}</span>}
              </div>
              <p className="text-gray-700 whitespace-pre-wrap">{f.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-gray-400 shrink-0">{label}</dt>
      <dd className="text-right">{children}</dd>
    </div>
  );
}

// ─── Edit modal ────────────────────────────────────────────────────────────────

function EditClientModal({ client, onClose, onUpdated }: { client: Client; onClose: () => void; onUpdated: () => void }) {
  const [form, setForm] = useState({
    business_name: client.business_name,
    domain: client.domain ?? '',
    contact_name: client.contact_name ?? '',
    contact_email: client.contact_email ?? '',
    contact_phone: client.contact_phone ?? '',
    notes: client.notes ?? '',
    status: client.status,
  });
  const [error, setError] = useState('');
  const updateClient = useUpdateClient();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.business_name.trim()) { setError('Nombre obligatorio'); return; }
    setError('');
    updateClient.mutate({
      id: client.id,
      data: {
        business_name: form.business_name.trim(),
        domain: form.domain.trim() || null,
        contact_name: form.contact_name.trim() || null,
        contact_email: form.contact_email.trim() || null,
        contact_phone: form.contact_phone.trim() || null,
        notes: form.notes.trim() || null,
        status: form.status,
      },
    }, {
      onSuccess: () => onUpdated(),
      onError: (e) => setError(e instanceof Error ? e.message : 'Error'),
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">Editar Tenant</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del negocio *</label>
            <input type="text" value={form.business_name} onChange={e => setForm(f => ({ ...f, business_name: e.target.value }))} className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Dominio</label>
            <input type="text" value={form.domain} onChange={e => setForm(f => ({ ...f, domain: e.target.value }))} className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contacto</label>
              <input type="text" value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
              <input type="text" value={form.contact_phone} onChange={e => setForm(f => ({ ...f, contact_phone: e.target.value }))} className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input type="email" value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
            <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value as Client['status'] }))} className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400">
              <option value="cold_lead">Cold Lead</option>
              <option value="warm_lead">Warm Lead</option>
              <option value="active">Activo</option>
              <option value="churned">Perdido</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notas internas</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={3} className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Cancelar</button>
            <button type="submit" disabled={updateClient.isPending} className="flex items-center gap-2 px-5 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-lg text-sm font-medium disabled:opacity-50">
              {updateClient.isPending && <Loader2 size={14} className="animate-spin" />}
              Guardar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Helpers & badges ────────────────────────────────────────────────────────

function fmtDate(iso: string) {
  try { return format(new Date(iso), 'yyyy-MM-dd HH:mm'); } catch { return iso; }
}
function fmtDay(iso: string) {
  try { return format(new Date(iso), 'dd/MM/yy'); } catch { return iso; }
}
function formatMoney(amount: number | null | undefined, currency: string | null = 'EUR') {
  if (amount == null) return '—';
  const cur = currency || 'EUR';
  try {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: cur, maximumFractionDigits: 0 }).format(amount);
  } catch {
    return `${amount} ${cur}`;
  }
}
function countAmenities(json: string | null): number {
  if (!json) return 0;
  try {
    const v = JSON.parse(json);
    return Array.isArray(v) ? v.length : 0;
  } catch { return 0; }
}

function ClientStatusBadge({ status }: { status: string }) {
  const v: Record<string, string> = { cold_lead: 'bg-slate-100 text-slate-600', warm_lead: 'bg-amber-100 text-amber-700', active: 'bg-green-100 text-green-700', churned: 'bg-gray-100 text-gray-500', lead: 'bg-blue-100 text-blue-700' };
  const l: Record<string, string> = { cold_lead: 'Cold Lead', warm_lead: 'Warm Lead', active: 'Activo', churned: 'Perdido', lead: 'Lead' };
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${v[status] ?? 'bg-gray-100 text-gray-600'}`}>{l[status] ?? status}</span>;
}

function OnboardingBadge({ status }: { status: string | null }) {
  if (!status) return null;
  const v: Record<string, string> = { not_started: 'bg-gray-100 text-gray-500', in_progress: 'bg-blue-100 text-blue-700', submitted: 'bg-amber-100 text-amber-700', approved: 'bg-green-100 text-green-700' };
  const l: Record<string, string> = { not_started: 'Sin empezar', in_progress: 'Alta en curso', submitted: 'Alta enviada', approved: 'Alta aprobada' };
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${v[status] ?? 'bg-gray-100 text-gray-600'}`}>{l[status] ?? status}</span>;
}

function PropertyStatusBadge({ status }: { status: string }) {
  const active = status === 'active';
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{active ? 'Activa' : 'Pausada'}</span>;
}

function BookingStatusBadge({ status }: { status: string }) {
  const v: Record<string, string> = { pending: 'bg-yellow-100 text-yellow-700', confirmed: 'bg-green-100 text-green-700', cancelled: 'bg-red-100 text-red-700', completed: 'bg-blue-100 text-blue-700', hold: 'bg-purple-100 text-purple-700' };
  const l: Record<string, string> = { pending: 'Pendiente', confirmed: 'Confirmada', cancelled: 'Cancelada', completed: 'Completada', hold: 'Bloqueo' };
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${v[status] ?? 'bg-gray-100 text-gray-600'}`}>{l[status] ?? status}</span>;
}

function FeedbackStatusBadge({ status }: { status: string | null }) {
  const s = status ?? 'new';
  const v: Record<string, string> = { new: 'bg-blue-100 text-blue-700', read: 'bg-gray-100 text-gray-500', resolved: 'bg-green-100 text-green-700' };
  const l: Record<string, string> = { new: 'Nuevo', read: 'Leído', resolved: 'Resuelto' };
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${v[s] ?? 'bg-gray-100 text-gray-600'}`}>{l[s] ?? s}</span>;
}

function PaymentBadge({ status, paidAt }: { status: string | null; paidAt: string | null }) {
  if (status === 'paid') {
    return <span className="text-emerald-700 flex items-center gap-1"><CheckCircle2 size={13} /> Pagado{paidAt ? ` · ${fmtDate(paidAt)}` : ''}</span>;
  }
  return <span className="text-gray-400 flex items-center gap-1"><XCircle size={13} /> Sin pagar</span>;
}

function ConnectBadge({ status, hasAccount }: { status: string | null; hasAccount: boolean }) {
  if (!hasAccount) return <span className="text-gray-400 flex items-center gap-1"><XCircle size={13} /> No conectado</span>;
  if (status === 'active') return <span className="text-emerald-700 flex items-center gap-1"><CheckCircle2 size={13} /> Activo (cobra)</span>;
  if (status === 'restricted') return <span className="text-amber-700 flex items-center gap-1"><XCircle size={13} /> Restringido</span>;
  return <span className="text-amber-700 flex items-center gap-1"><Loader2 size={13} /> Pendiente</span>;
}
